import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { Link } from "wouter";
import { Mail, Phone, ChevronRight, Trash2, Copy, Check } from "lucide-react";
import { useState } from "react";

interface MobileContactCardProps {
  id: string;
  name: string;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  companyName: string;
  category: string;
  initials: string;
  isSelected?: boolean;
  onSelect?: () => void;
  onDelete?: () => void;
  /** Defaults to the tint the Contacts page used to pass in. */
  getCategoryAvatarStyle?: (category: string) => string;
  getCategoryBadgeStyle: (category: string) => string;
}

const defaultCategoryAvatarStyle = (category: string) => {
  switch (category) {
    case "Buyer":
      return "bg-secondary";
    case "Client":
      return "gold-gradient";
    case "COI":
      return "bg-teal-600";
    default:
      return "bg-muted";
  }
};

export function MobileContactCard({
  id,
  name,
  title,
  email,
  phone,
  companyName,
  category,
  initials,
  isSelected,
  onSelect,
  onDelete,
  getCategoryAvatarStyle = defaultCategoryAvatarStyle,
  getCategoryBadgeStyle,
}: MobileContactCardProps) {
  const [copiedEmail, setCopiedEmail] = useState(false);

  const handleCopyEmail = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (email) {
      navigator.clipboard.writeText(email);
      setCopiedEmail(true);
      setTimeout(() => setCopiedEmail(false), 2000);
    }
  };

  return (
    <Card className={cn(
      "overflow-hidden transition-all",
      isSelected && "border-primary ring-1 ring-primary"
    )}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {/* Selection checkbox */}
          {onSelect && (
            <div className="pt-1">
              <Checkbox
                checked={isSelected}
                onCheckedChange={onSelect}
                aria-label={`Select ${name}`}
              />
            </div>
          )}
          
          {/* Avatar */}
          <Avatar className={cn("w-12 h-12 shrink-0", getCategoryAvatarStyle(category))}>
            <AvatarImage src="" />
            <AvatarFallback className="text-sm font-semibold bg-transparent text-white">
              {initials}
            </AvatarFallback>
          </Avatar>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <Link href={`/contacts/${id}`}>
                  <h3 className="font-semibold text-foreground truncate hover:text-primary-text transition-colors">
                    {name}
                  </h3>
                </Link>
                {title && (
                  <p className="text-sm text-muted-foreground truncate">{title}</p>
                )}
              </div>
              <Badge className={cn("shrink-0 text-xs", getCategoryBadgeStyle(category))}>
                {category}
              </Badge>
            </div>
            
            {/* Company */}
            <p className="text-sm font-medium text-foreground mt-1 truncate">
              {companyName}
            </p>
            
            {/* Contact info */}
            <div className="flex flex-wrap gap-3 mt-2">
              {email && (
                <div className="flex items-center gap-1">
                  <a
                    href={`mailto:${email}`}
                    className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary-text transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Mail className="w-3.5 h-3.5" />
                    <span className="truncate max-w-[120px]">{email}</span>
                  </a>
                  <button
                    onClick={handleCopyEmail}
                    className="p-1 rounded hover:bg-muted transition-colors"
                    title="Copy email"
                  >
                    {copiedEmail ? (
                      <Check className="w-3 h-3 text-green-500" />
                    ) : (
                      <Copy className="w-3 h-3 text-muted-foreground" />
                    )}
                  </button>
                </div>
              )}
              {phone && (
                <a 
                  href={`tel:${phone}`}
                  className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary-text transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Phone className="w-3.5 h-3.5" />
                  <span>{phone}</span>
                </a>
              )}
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex flex-col items-center gap-2 shrink-0">
            {onDelete && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
            <Link href={`/contacts/${id}`}>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </Link>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Skeleton for mobile contact cards
export function MobileContactCardSkeleton() {
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-5 h-5 mt-1 rounded bg-muted animate-pulse" />
          <div className="w-12 h-12 rounded-full bg-muted animate-pulse shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-5 w-32 bg-muted rounded animate-pulse" />
            <div className="h-4 w-24 bg-muted rounded animate-pulse" />
            <div className="h-4 w-20 bg-muted rounded animate-pulse" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
